import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { SYSTEM_VERSION } from '../version';

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login({ username, password });
      
      // Verify auth state is updated (AuthContext will re-render)
      // The token is now saved and isAuthenticated will return true
      const token = localStorage.getItem('token');
      if (token) {
        navigate('/dashboard');
      } else {
        // If no token after login, wait briefly and check again
        // This handles the async state update in AuthContext
        const checkAuth = () => {
          const retryToken = localStorage.getItem('token');
          if (retryToken) {
            navigate('/dashboard');
          } else {
            setError('خطا در برقراری نشست کاربری');
          }
        };
        setTimeout(checkAuth, 100);
      }
    } catch (err: any) {
      let msg = 'نام کاربری یا رمز عبور اشتباه است';
      if (!err.response) {
        msg = 'خطا در اتصال به سرور. لطفاً مطمئن شوید Backend در حال اجراست.';
      } else if (err.response?.data) {
        const d = err.response.data;
        msg = d.message ?? d.error?.message ?? msg;
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ px: { xs: 2, sm: 3 } }}>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 2,
        }}
      >
        <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 }, width: '100%' }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            ورود به سیستم
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            سیستم جامع مالی و اداری
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="نام کاربری"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              margin="normal"
              required
              autoFocus
            />
            <TextField
              fullWidth
              label="رمز عبور"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                      aria-label={showPassword ? 'مخفی کردن رمز عبور' : 'نمایش رمز عبور'}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? 'در حال ورود...' : 'ورود'}
            </Button>
          </form>

          <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #eee', textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              نسخه سامانه: {SYSTEM_VERSION}
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;